import { useState } from 'react';
import useSWR from 'swr';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import type { RlcEnvironment, RlcEnvironmentStatus, RlcProbe } from '../../types';
import { ErrorNote, HarmCheckSection, InternalDomainsSection, LiveConfigSection, ModelServiceSections, RestrictedListSection, RunningSection, Section, UsageSection } from './EnvironmentPanels';

function useEnvironments() {
  const swr = useSWR('admin/environments', () => adminApi.listEnvironments(), {
    refreshInterval: 60_000,
    keepPreviousData: true,
  });
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      await swr.mutate(adminApi.listEnvironments({ refresh: true }), { revalidate: false });
    } finally {
      setRefreshing(false);
    }
  };
  return { ...swr, refresh, refreshing };
}

export function EnvironmentsPage() {
  const { data, error, refresh, refreshing } = useEnvironments();
  const envs = data?.environments ?? [];
  const installable = envs.filter((e) => e.addin.manifest);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-100">Environments</h1>
          <p className="text-sm text-gray-400 mt-1 max-w-3xl">
            Every deployed Restricted-List Classifier environment: where it lives, what version it is
            running right now, how to install its Outlook add-in, and which restricted list it scans against.
          </p>
        </div>
        <RefreshButton checkedAt={data?.checkedAt} onClick={refresh} busy={refreshing} />
      </div>

      {error && <ErrorNote>Could not load the catalog: {error.message}</ErrorNote>}

      <div className="bg-gray-900 border border-gray-800 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-2 font-medium">Environment</th>
              <th className="px-4 py-2 font-medium">API server</th>
              <th className="px-4 py-2 font-medium">Outlook add-in</th>
              <th className="px-4 py-2 font-medium">Organization / database</th>
              <th className="px-4 py-2 font-medium">Restricted list</th>
            </tr>
          </thead>
          <tbody>
            {envs.map((e) => (
              <tr key={e.key} className="border-b border-gray-800 align-top hover:bg-gray-800/40">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/environments/${e.key}`} className="text-blue-400 hover:underline font-medium">
                      {e.name}
                    </Link>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{e.audience}</div>
                  {e.notes.some((n) => n.level === 'warning') && (
                    <div className="text-xs text-amber-300 mt-1">Has a warning. Open for details.</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <ProbeLine probe={e.api} label={e.api.version ? `v${e.api.version}` : 'version unknown'} />
                  <HostLink url={e.apiUrl} />
                </td>
                <td className="px-4 py-3">
                  <ProbeLine
                    probe={e.addin}
                    label={
                      e.addin.manifest
                        ? `${e.addin.manifest.displayName ?? 'Add-in'} v${e.addin.manifest.version ?? '?'}`
                        : 'no manifest'
                    }
                  />
                  <HostLink url={e.addinUrl} />
                  {e.addin.sharesIdWith.length > 0 && (
                    <div className="text-xs text-amber-300 mt-1">
                      Same add-in id as {namesOf(envs, e.addin.sharesIdWith)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-300">
                  <div>{e.organization ?? <span className="text-gray-500">not recorded</span>}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {e.database ? <code>{e.database}</code> : 'database not recorded'} · declared
                  </div>
                </td>
                <td className="px-4 py-3">
                  {e.restrictedList?.error ? (
                    <span className="text-red-300">Could not read ({e.restrictedList.error})</span>
                  ) : e.restrictedList ? (
                    <Link to={`/admin/environments/${e.key}/restricted-list`} className="text-blue-400 hover:underline">
                      {e.restrictedList.lists.length === 0
                        ? 'No list'
                        : e.restrictedList.lists
                            .map((l) => `${l.name || 'Unnamed list'} (${l.total.toLocaleString()} terms)`)
                            .join(', ')}
                    </Link>
                  ) : (
                    <span className="text-gray-500">Not connected</span>
                  )}
                </td>
              </tr>
            ))}
            {data && envs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No environments in the catalog</td></tr>
            )}
            {!data && !error && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Checking environments…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Section title="Which environment do I have installed?">
        <p className="text-sm text-gray-400 max-w-3xl">
          Every environment's add-in is called HarmCheck, so the name will not tell you. The version will:
          in Outlook, open the add-in list (
          <a href="https://aka.ms/olksideload" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">aka.ms/olksideload</a>
          {' '}→ My add-ins) and read the description, which starts with “HarmCheck” and a version number.
          Match it here.
        </p>
        <table className="text-sm mt-3">
          <thead>
            <tr className="text-gray-400 text-left">
              <th className="pr-8 py-1 font-medium">Description starts with</th>
              <th className="pr-8 py-1 font-medium">Environment</th>
              <th className="py-1 font-medium">Add-in id</th>
            </tr>
          </thead>
          <tbody>
            {installable.map((e) => (
              <tr key={e.key} className="border-t border-gray-800">
                <td className="pr-8 py-1.5 text-gray-100">HarmCheck {e.addin.manifest?.version}</td>
                <td className="pr-8 py-1.5">
                  <Link to={`/admin/environments/${e.key}`} className="text-blue-400 hover:underline">{e.name}</Link>
                </td>
                <td className="py-1.5 text-gray-400"><code className="text-xs">{e.addin.manifest?.id}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasDuplicateVersions(installable) && (
          <p className="text-xs text-amber-300 mt-3">
            Two environments are serving the same add-in version right now, so the version alone cannot tell them apart.
          </p>
        )}
      </Section>

      {data && data.roadmap.length > 0 && (
        <Section title="Not in the catalog yet">
          <p className="text-sm text-gray-400 max-w-3xl">
            What this page cannot show today, and the repo that has to change before it can.
          </p>
          <ul className="mt-3 divide-y divide-gray-800">
            {data.roadmap.map((item) => (
              <li key={item.title} className="py-2.5 flex items-start gap-3">
                <span className="mt-0.5 text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded border bg-gray-800 text-gray-300 border-gray-700 shrink-0">
                  To do
                </span>
                <div>
                  <div className="text-sm text-gray-100">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5 max-w-3xl">{item.detail}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Needs: {item.needs}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

const DETAIL_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'restricted-list', label: 'Restricted list' },
  { key: 'test', label: 'Test a message' },
  { key: 'usage', label: 'Usage' },
  { key: 'config', label: 'Configuration' },
  { key: 'deployment', label: 'Deployment' },
  { key: 'install', label: 'Install add-in' },
] as const;
type DetailTab = (typeof DETAIL_TABS)[number]['key'];

export function EnvironmentDetailPage() {
  const { key = '', tab: tabParam } = useParams<{ key: string; tab?: string }>();
  const { hash } = useLocation();
  const { data, error, refresh, refreshing } = useEnvironments();
  const envs = data?.environments ?? [];
  const env = envs.find((e) => e.key === key);

  const validTab = !tabParam || DETAIL_TABS.some((t) => t.key === tabParam);
  const tab = (validTab && tabParam ? tabParam : 'overview') as DetailTab;

  // A tab mounts the first time it is opened and then stays mounted (hidden) while
  // you look at other tabs, so a half-typed test message, its result, or pending
  // list edits survive a tab switch. Unvisited tabs never fetch anything.
  const [visited, setVisited] = useState<{ key: string; tabs: Set<DetailTab> }>({ key, tabs: new Set([tab]) });
  if (visited.key !== key) {
    setVisited({ key, tabs: new Set([tab]) });
  } else if (!visited.tabs.has(tab)) {
    setVisited({ key, tabs: new Set([...visited.tabs, tab]) });
  }

  // Old links pointed at #restricted-list on the single long page.
  if (!tabParam && hash === '#restricted-list') {
    return <Navigate to={`/admin/environments/${key}/restricted-list`} replace />;
  }
  if (!validTab) {
    return <Navigate to={`/admin/environments/${key}`} replace />;
  }

  if (error) return <ErrorNote>Could not load the catalog: {error.message}</ErrorNote>;
  if (!data) return <div className="text-gray-500 text-sm">Checking environment…</div>;
  if (!env) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-gray-100">Unknown environment</h1>
        <Link to="/admin/environments" className="text-blue-400 hover:underline text-sm">Back to environments</Link>
      </div>
    );
  }

  const warnings = env.notes.filter((n) => n.level === 'warning');
  const deploymentShared = env.components.some((c) => c.sourceSharedWith.length > 0 || c.imageSharedWith.length > 0)
    || env.sharedNotes.length > 0 || env.addin.sharesIdWith.length > 0;
  const listTerms = env.restrictedList && !env.restrictedList.error
    ? env.restrictedList.lists.reduce((n, l) => n + l.total, 0)
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/environments" className="text-xs text-gray-500 hover:text-gray-300">← Environments</Link>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl font-semibold text-gray-100">{env.name}</h1>
            <StatusBadge status={env.status} />
          </div>
          <p className="text-sm text-gray-400 mt-1 max-w-3xl">{env.purpose}</p>
          <p className="text-xs text-gray-500 mt-1">For: {env.audience}</p>
        </div>
        <RefreshButton checkedAt={data.checkedAt} onClick={refresh} busy={refreshing} />
      </div>

      {tab !== 'overview' && warnings.length > 0 && (
        <Link
          to={`/admin/environments/${env.key}`}
          className="block rounded border px-4 py-2 text-sm bg-amber-900/20 border-amber-700/60 text-amber-100 hover:bg-amber-900/30"
        >
          {warnings.length === 1 ? 'This environment has a warning.' : `This environment has ${warnings.length} warnings.`}{' '}
          <span className="underline">See Overview</span>
        </Link>
      )}

      <nav className="border-b border-gray-800 flex gap-1 overflow-x-auto" aria-label="Environment sections">
        {DETAIL_TABS.map((t) => (
          <Link
            key={t.key}
            to={t.key === 'overview' ? `/admin/environments/${env.key}` : `/admin/environments/${env.key}/${t.key}`}
            aria-current={tab === t.key ? 'page' : undefined}
            className={`px-3 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${
              tab === t.key ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
            {t.key === 'overview' && warnings.length > 0 && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1.5 align-middle" title="Has warnings" />
            )}
            {t.key === 'restricted-list' && listTerms !== undefined && (
              <span className="text-gray-500 ml-1">({listTerms.toLocaleString()})</span>
            )}
            {t.key === 'deployment' && deploymentShared && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1.5 align-middle" title="Shares something with another environment" />
            )}
          </Link>
        ))}
      </nav>

      {DETAIL_TABS.filter((t) => visited.key === env.key && visited.tabs.has(t.key)).map((t) => (
        <div key={t.key} hidden={tab !== t.key}>
          {t.key === 'overview' && <OverviewTab env={env} envs={envs} />}
          {t.key === 'restricted-list' && <RestrictedListSection env={env} />}
          {t.key === 'test' && <HarmCheckSection env={env} />}
          {t.key === 'usage' && <UsageSection env={env} />}
          {t.key === 'config' && <ConfigTab env={env} />}
          {t.key === 'deployment' && <DeploymentTab env={env} envs={envs} />}
          {t.key === 'install' && <InstallTab env={env} />}
        </div>
      ))}
    </div>
  );
}

function OverviewTab({ env, envs }: { env: RlcEnvironment; envs: RlcEnvironment[] }) {
  const m = env.addin.manifest;
  const rl = env.restrictedList;
  return (
    <div className="space-y-4">
      {env.notes.map((n) => (
        <div
          key={n.date + n.text}
          className={`rounded border px-4 py-3 text-sm ${
            n.level === 'warning'
              ? 'bg-amber-900/20 border-amber-700/60 text-amber-100'
              : 'bg-gray-900 border-gray-800 text-gray-300'
          }`}
        >
          <span className="text-xs opacity-70 mr-2">{n.date}</span>
          {n.text}
        </div>
      ))}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="API server" badge="Live">
          <ProbeLine probe={env.api} label={env.api.status === 'up' ? 'Responding' : 'Not responding'} />
          <Facts
            rows={[
              ['Version', env.api.version ? `v${env.api.version}` : 'unknown'],
              ['URL', <ExternalLink key="u" href={env.apiUrl} />],
              ['API docs', <ExternalLink key="d" href={env.api.docsUrl} />],
              ['Response time', `${env.api.latencyMs} ms`],
            ]}
          />
        </Section>

        <Section title="Outlook add-in" badge="Live">
          <ProbeLine probe={env.addin} label={m ? 'Serving a manifest' : 'Not serving a manifest'} />
          <Facts
            rows={[
              ['Name in Outlook', m?.displayName ?? 'unknown'],
              ['Version', m?.version ? `v${m.version}` : 'unknown'],
              ['Add-in id', m?.id ? <code key="i" className="text-xs">{m.id}</code> : 'unknown'],
              ['Description', m?.description ?? 'unknown'],
              ['Manifest', <ExternalLink key="m" href={env.addin.manifestUrl} />],
              ['Hosts in manifest', m?.hosts.length ? m.hosts.join(', ') : 'unknown'],
            ]}
          />
          {env.addin.sharesIdWith.length > 0 && (
            <p className="text-xs text-amber-300 mt-3">
              Same add-in id as {namesOf(envs, env.addin.sharesIdWith)}. Outlook treats them as one add-in,
              so installing this one replaces the other.
            </p>
          )}
          {m && (
            <Link to={`/admin/environments/${env.key}/install`} className="inline-block text-sm text-blue-400 hover:underline mt-3">
              How to install it →
            </Link>
          )}
        </Section>

        <Section title="At a glance" badge="Declared">
          <Facts
            rows={[
              ['Organization', env.organization ?? 'not recorded'],
              ['Database', env.database ? <code key="db" className="text-xs">{env.database}</code> : 'not recorded'],
              ['Restricted list', rl?.error
                ? <span key="rl" className="text-red-300">Could not read ({rl.error})</span>
                : rl
                  ? (
                    <Link key="rl" to={`/admin/environments/${env.key}/restricted-list`} className="text-blue-400 hover:underline">
                      {rl.lists.length === 0
                        ? 'No list'
                        : rl.lists.map((l) => `${l.name || 'Unnamed list'} (${l.total.toLocaleString()} terms)`).join(', ')}
                    </Link>
                  )
                  : 'not connected'],
              ['Helm release', <code key="hr" className="text-xs">{env.deployment.helmRelease}</code>],
              ['Namespace', <code key="ns" className="text-xs">{env.deployment.namespace}</code>],
            ]}
          />
          <div className="flex gap-4 mt-3 text-sm">
            <Link to={`/admin/environments/${env.key}/config`} className="text-blue-400 hover:underline">Configuration →</Link>
            <Link to={`/admin/environments/${env.key}/deployment`} className="text-blue-400 hover:underline">Deployment →</Link>
          </div>
        </Section>

        <ModelServiceSections env={env} />
      </div>
    </div>
  );
}

function ConfigTab({ env }: { env: RlcEnvironment }) {
  return (
    <div className="space-y-4">
      <Section title="Configuration" badge="Declared">
        <p className="text-xs text-gray-500 max-w-3xl">
          Copied by hand from the Helm values, not read from the running deployment. It can be out of date.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
          <Facts
            rows={[
              ['Organization', env.organization ?? 'not recorded'],
              ['Database', env.database ? <code key="db" className="text-xs">{env.database}</code> : 'not recorded'],
              ...env.features.map((f): [string, React.ReactNode] => [f.label, f.value]),
            ]}
          />
          <Facts
            rows={[
              ['Cluster', env.deployment.cluster],
              ['Namespace', <code key="ns" className="text-xs">{env.deployment.namespace}</code>],
              ['Helm release', <code key="hr" className="text-xs">{env.deployment.helmRelease}</code>],
              ['Chart', env.chart.ref
                ? <GitHubLink key="ch" repo={env.chart.repo} path={`tree/${env.chart.ref}`} label={`${env.chart.repo} @ ${env.chart.ref}`} />
                : <GitHubLink key="ch" repo={env.chart.repo} label={env.chart.repo} />],
              ['Values file', env.chart.valuesFile
                ? <GitHubLink key="vf" repo={env.chart.repo} path={`blob/main/${env.chart.valuesFile}`} label={env.chart.valuesFile} />
                : 'none in git'],
              ...(env.chart.note ? [['Note', env.chart.note] as [string, React.ReactNode]] : []),
            ]}
          />
        </div>
      </Section>
      <LiveConfigSection env={env} />
      <InternalDomainsSection env={env} />
    </div>
  );
}

function DeploymentTab({ env, envs }: { env: RlcEnvironment; envs: RlcEnvironment[] }) {
  return (
    <div className="space-y-4">
      <RunningSection env={env} />
      <Section title="Where it comes from" badge="Declared">
        <p className="text-xs text-gray-500 max-w-3xl">
          The repo and branch or tag each service is built from, the image it runs, and what triggers a deploy.
          Anything another environment also uses is flagged, because changing it here changes it there.
        </p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400 text-left">
                <th className="py-1.5 pr-4 font-medium">Service</th>
                <th className="py-1.5 pr-4 font-medium">Source</th>
                <th className="py-1.5 pr-4 font-medium">Image</th>
                <th className="py-1.5 font-medium">Deploys</th>
              </tr>
            </thead>
            <tbody>
              {env.components.map((c) => (
                <tr key={c.name} className="border-b border-gray-800/60 align-top">
                  <td className="py-2 pr-4 text-gray-100 whitespace-nowrap">{c.name}</td>
                  <td className="py-2 pr-4">
                    {c.repo ? (
                      c.ref
                        ? <GitHubLink repo={c.repo} path={`tree/${c.ref}`} label={`${c.repo} @ ${c.ref}`} />
                        : <><GitHubLink repo={c.repo} label={c.repo} /> <span className="text-gray-500 text-xs">branch not recorded</span></>
                    ) : (
                      <span className="text-gray-500">not recorded</span>
                    )}
                    {c.sourceSharedWith.length > 0 && (
                      <div className="text-xs text-amber-300 mt-1">Same branch as {namesOf(envs, c.sourceSharedWith)}</div>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <code className="text-xs text-gray-200 break-all">{c.image.replace(/^[^/]+\//, '')}</code>
                    {c.imageSharedWith.length > 0 && (
                      <div className="text-xs text-amber-300 mt-1">Same image tag as {namesOf(envs, c.imageSharedWith)}</div>
                    )}
                  </td>
                  <td className="py-2 text-gray-400 text-xs">{c.deploys}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(env.sharedNotes.length > 0 || env.addin.sharesIdWith.length > 0) && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-gray-100">Also shared</h3>
            <ul className="mt-1 space-y-1 text-xs text-amber-200 list-disc list-inside max-w-3xl">
              {env.addin.sharesIdWith.length > 0 && (
                <li>The Outlook add-in id, with {namesOf(envs, env.addin.sharesIdWith)}. Installing one replaces the other.</li>
              )}
              {env.sharedNotes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        )}
      </Section>
    </div>
  );
}

function InstallTab({ env }: { env: RlcEnvironment }) {
  const m = env.addin.manifest;
  return (
    <Section title="Install the add-in">
      {!m ? (
        <p className="text-sm text-gray-400">This environment is not serving a manifest, so there is nothing to install.</p>
      ) : (
        <>
          {env.status !== 'active' && (
            <p className="text-sm text-amber-300 mb-3">This environment is {env.status}. Install it only if you have a specific reason to.</p>
          )}
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300 max-w-3xl">
            <li>
              <a
                href={adminApi.environmentManifestUrl(env.key)}
                className="inline-block px-3 py-1 rounded bg-blue-900/50 text-blue-100 border border-blue-800 hover:bg-blue-900"
              >
                Download the manifest
              </a>
              <span className="text-gray-500 ml-2">saved as harmcheck-{env.key}-{m.version}.xml</span>
            </li>
            <li>
              Open{' '}
              <a href="https://aka.ms/olksideload" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">aka.ms/olksideload</a>
              {' '}signed in to the mailbox you test with. Outlook opens, then the Add-Ins for Outlook dialog a few seconds later.
            </li>
            <li>Select <b>My add-ins</b>. If another HarmCheck is listed under Custom Addins, remove it first.</li>
            <li>Under Custom Addins, choose <b>Add a custom add-in</b> → <b>Add from File</b>, pick the downloaded file, and select <b>Install</b>.</li>
            <li>Check the new entry's description starts with “HarmCheck {m.version}”.</li>
          </ol>
          <p className="text-xs text-gray-500 mt-3 max-w-3xl">
            A HarmCheck that an administrator deployed to your mailbox is listed as admin-managed and cannot be
            removed from this dialog. Ask whoever manages the tenant to remove it.
          </p>
        </>
      )}
    </Section>
  );
}

function namesOf(envs: RlcEnvironment[], keys: string[]) {
  return keys.map((k) => envs.find((e) => e.key === k)?.name ?? k).join(', ');
}

function hasDuplicateVersions(envs: RlcEnvironment[]) {
  const versions = envs.map((e) => e.addin.manifest?.version);
  return new Set(versions).size !== versions.length;
}

function StatusBadge({ status }: { status: RlcEnvironmentStatus }) {
  const classes =
    status === 'active'
      ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/60'
      : status === 'legacy'
        ? 'bg-amber-900/40 text-amber-200 border-amber-700/60'
        : 'bg-gray-800 text-gray-400 border-gray-700';
  return (
    <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded border ${classes}`}>
      {status}
    </span>
  );
}

function ProbeLine({ probe, label }: { probe: RlcProbe; label: string }) {
  const up = probe.status === 'up';
  return (
    <div className="flex items-center gap-2 text-gray-100">
      <span className={`w-2 h-2 rounded-full shrink-0 ${up ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <span>{label}</span>
      {!up && probe.error && <span className="text-xs text-red-300">({probe.error})</span>}
    </div>
  );
}

function HostLink({ url }: { url: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block text-xs text-gray-500 hover:text-gray-300 mt-1">
      {url.replace(/^https?:\/\//, '')}
    </a>
  );
}

function GitHubLink({ repo, path, label }: { repo: string; path?: string; label: string }) {
  return (
    <a
      href={`https://github.com/Alpha-Girls/${repo}${path ? `/${path}` : ''}`}
      target="_blank"
      rel="noreferrer"
      className="text-blue-400 hover:underline break-all"
    >
      {label}
    </a>
  );
}

function ExternalLink({ href }: { href: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline break-all">
      {href.replace(/^https?:\/\//, '')}
    </a>
  );
}

function RefreshButton({ checkedAt, onClick, busy }: { checkedAt?: string; onClick: () => void; busy: boolean }) {
  return (
    <div className="flex items-center gap-3 shrink-0">
      {checkedAt && <span className="text-xs text-gray-500">Checked {new Date(checkedAt).toLocaleTimeString()}</span>}
      <button
        onClick={onClick}
        disabled={busy}
        className="px-3 py-1.5 text-sm rounded bg-gray-800 text-gray-100 border border-gray-700 hover:bg-gray-700 disabled:opacity-50"
      >
        {busy ? 'Checking…' : 'Check now'}
      </button>
    </div>
  );
}


function Facts({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <dl className="mt-3 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-4 py-1.5 border-t border-gray-800">
          <dt className="w-40 shrink-0 text-gray-500">{label}</dt>
          <dd className="text-gray-200 min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
